import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/tiposolicitacao.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const TipoSolicitacaoManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_tp_solicitacao: '',
    id_tp_projeto: '',
    descr_tp_solic: '',
    ativo: 'true',
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [tiposProjeto, setTiposProjeto] = useState([]); // New state for project types

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'tipo de solicitação', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/tipo-solicitacao/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'tipo de solicitação', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'tipo de solicitação');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'tipo de solicitação', { id });
    try {
      const response = await api.delete(`/tipo-solicitacao/${id}`);
      logarSucesso('Excluir', 'tipo de solicitação', response.data);
      setDados(currentData => currentData.filter(item => item.id_tp_solicitacao !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'tipo de solicitação');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const getColunas = useMemo(() => {
    const tiposProjetoMap = tiposProjeto.reduce((acc, curr) => {
      acc[curr.id_tp_projeto] = curr.descr_tp_projeto;
      return acc;
    }, {});

    const colunasBase = [
      {
        title: 'ID Tipo Solicitação',
        field: 'id_tp_solicitacao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'Tipo Projeto',
        field: 'id_tp_projeto',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Tipo...',
        sorter: 'string',
        formatter: (cell) => {
          const val = cell.getValue();
          return tiposProjetoMap[val] ? `${tiposProjetoMap[val]} (${val})` : val;
        },
        editor: 'list',
        editorParams: {
          values: tiposProjetoMap,
          autocomplete: true,
          clearable: true
        }
      },
      {
        title: 'Descrição',
        field: 'descr_tp_solic',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
      },
      {
        title: 'Ativo',
        field: 'ativo',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar status...',
        sorter: 'boolean',
        formatter: 'tickCross'
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_tp_solicitacao, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_tp_solicitacao}"?`)) {
            excluirRegistro(data.id_tp_solicitacao);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => ({
        ...col,
        editor: col.editor || 'input'
      }))
    ];
  }, [atualizarRegistro, excluirRegistro, tiposProjeto]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas,
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_tp_solicitacao'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/tipo-solicitacao');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/tipo-solicitacao');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchTiposProjeto = useCallback(async () => {
    try {
      const response = await api.get('/tipo-projeto');
      setTiposProjeto(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar tipos de projeto: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchTiposProjeto();
  }, [verificarAPI, fetchTiposProjeto]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, getColunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_tp_solicitacao, id_tp_projeto, descr_tp_solic, ativo } = formData;

    const camposFaltando = [];
    if (!id_tp_solicitacao) camposFaltando.push('ID Tipo Solicitação');
    if (!id_tp_projeto) camposFaltando.push('ID Tipo Projeto');
    if (!descr_tp_solic) camposFaltando.push('Descrição');
    if (!ativo) camposFaltando.push('Ativo');

    if (camposFaltando.length > 0) {
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposFaltando.join(', ')}`);
      return;
    }

    const dataToSend = {
      id_tp_solicitacao,
      id_tp_projeto,
      descr_tp_solic: descr_tp_solic === '' ? null : descr_tp_solic,
      ativo
    };

    logarRequisicao('Adicionar', 'tipo de solicitação', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/tipo-solicitacao', dataToSend);
      logarSucesso('Adicionar', 'tipo de solicitação', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_tp_solicitacao: '', id_tp_projeto: '', descr_tp_solic: '', ativo: 'true' });
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'tipo de solicitação');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'tipo_solicitacao.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Tipos de Solicitação</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_tipo_solicitacao</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/tipos de solicitacao.mov" buttonText="Ver Tutorial Tipos de Solicitação" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_tp_solicitacao">ID Tipo Solicitação:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="id_tp_solicitacao" name="id_tp_solicitacao" value={formData.id_tp_solicitacao} onChange={handleInputChange} placeholder="Ex: SOLIC_01" maxLength={255} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="id_tp_projeto">Tipo Projeto:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <select
              id="id_tp_projeto"
              name="id_tp_projeto"
              value={formData.id_tp_projeto}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
            >
              <option value="">Selecione um tipo de projeto</option>
              {tiposProjeto.map(tipo => (
                <option key={tipo.id_tp_projeto} value={tipo.id_tp_projeto}>
                  {tipo.descr_tp_projeto} ({tipo.id_tp_projeto})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="descr_tp_solic">Descrição:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="descr_tp_solic" name="descr_tp_solic" value={formData.descr_tp_solic} onChange={handleInputChange} placeholder="Ex: Manutenção" maxLength={100} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="ativo">Ativo:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <select
              id="ativo"
              name="ativo"
              value={formData.ativo}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
            >
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </select>
          </div>
          {/* Linha única: Adicionar, Recarregar, Exportar CSV + aviso de obrigatórios */}
          <div
            className="actions-row"
            style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', marginTop: '10px' }}
          >
            <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
            </button>
            <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
            </button>
            <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>

            <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
              <i className="fas fa-info-circle"></i>
              <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default TipoSolicitacaoManager;
